import { forwardRef } from "react";
import { cn } from "@/lib/utils";

/**
 * Single source of truth for the page's <main> landmark.
 *
 * Centralizes the id/tabIndex/role used by the global "Skip to main
 * content" link (see index.html + .skip-link styles in index.css). Using
 * this component everywhere guarantees the skip link's `#main-content`
 * target always exists, is focusable, and behaves consistently across
 * routes, so accessibility never silently regresses when a new page is
 * added.
 */
type MainProps = React.ComponentPropsWithoutRef<"main">;

const Main = forwardRef<HTMLElement, MainProps>(
  ({ className, children, ...rest }, ref) => (
    <main
      ref={ref}
      id="main-content"
      tabIndex={-1}
      role="main"
      className={cn("focus:outline-none", className)}
      {...rest}
    >
      {children}
    </main>
  ),
);
Main.displayName = "Main";

export default Main;
